import { Component, ChangeDetectionStrategy, inject, input, output, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { TuiIcon } from '@taiga-ui/core';
import { TuiAvatar } from '@taiga-ui/kit';
import { User } from '../../../core/models/user.model';
import { AuthService } from '../../../core/services/auth.service';
import { TimeIndicatorComponent } from '../time-indicator/time-indicator.component';
import { LabSummaryComponent } from '../lab-summary/lab-summary.component';

@Component({
  selector: 'app-header',
  standalone: true,
  imports: [CommonModule, TimeIndicatorComponent, LabSummaryComponent, TuiIcon, TuiAvatar],
  template: `
    <header class="header">
      <div class="header__left">
        <button
          type="button"
          class="header__icon-btn header__toggle"
          (click)="toggleSidebar.emit()"
          aria-label="Alternar menú lateral"
        >
          <tui-icon icon="@tui.menu" />
        </button>

        <div class="header__brand-mobile">
          <div class="header__brand-mark">R</div>
          <span class="header__brand-text">RPW Admin</span>
        </div>

        <div class="header__page">
          <h1 class="header__page-title">{{ pageTitle() || 'Inicio' }}</h1>
          @if (pageCrumbs().length > 0) {
            <nav class="header__crumbs" aria-label="Ruta de navegación">
              @for (crumb of pageCrumbs(); track $index; let last = $last) {
                <span class="header__crumb" [class.header__crumb--current]="last">{{ crumb }}</span>
                @if (!last) {
                  <tui-icon class="header__crumb-sep" icon="@tui.chevron-right" />
                }
              }
            </nav>
          } @else if (pageDescription()) {
            <p class="header__page-desc">{{ pageDescription() }}</p>
          }
        </div>
      </div>

      <div class="header__right">
        <app-lab-summary />

        <app-time-indicator />

        <!-- <button type="button" class="header__icon-btn" aria-label="Notificaciones">
          <tui-icon icon="@tui.bell" />
          <span class="header__dot"></span>
        </button> -->

        <div class="header__divider"></div>

        <div class="header__profile" (click)="$event.stopPropagation()">
          <button type="button" class="header__profile-btn" (click)="toggleMenu()">
            <div
              tuiAvatar
              size="s"
              class="header__avatar"
              [tuiAvatar]="currentUser()?.avatar || ''"
            >
              {{ currentUser()?.initials || '?' }}
            </div>
            <span class="header__profile-info">
              <span class="header__profile-name">{{ currentUser()?.name || 'Invitado' }}</span>
              <span class="header__profile-mail">{{ currentUser()?.email || '' }}</span>
            </span>
            <tui-icon
              class="header__profile-caret"
              [class.header__profile-caret--open]="showMenu()"
              icon="@tui.chevron-down"
            />
          </button>

          @if (showMenu()) {
            <div class="header__menu" role="menu">
              <div class="header__menu-head">
                <span class="header__menu-name">{{ currentUser()?.name || 'Invitado' }}</span>
                <span class="header__menu-mail">{{ currentUser()?.email || '' }}</span>
              </div>
              <button type="button" class="header__menu-item" (click)="handleMenuClick('Profile')">
                <tui-icon icon="@tui.user" />
                Perfil
              </button>
              <!-- <button type="button" class="header__menu-item" (click)="handleMenuClick('Settings')">
                <tui-icon icon="@tui.settings" />
                Configuración
              </button> -->
              <div class="header__menu-sep"></div>
              <button
                type="button"
                class="header__menu-item header__menu-item--danger"
                (click)="handleMenuClick('SignOut')"
              >
                <tui-icon icon="@tui.log-out" />
                Cerrar sesión
              </button>
            </div>
          }
        </div>
      </div>
    </header>
  `,
  styleUrl: './header.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HeaderComponent {
  private readonly router = inject(Router);
  private readonly authService = inject(AuthService);

  currentUser = input.required<User | null>();
  pageTitle = input<string>('');
  pageIcon = input<string>('');
  pageDescription = input<string | undefined>(undefined);
  pageCrumbs = input<string[]>([]);
  toggleSidebar = output<void>();

  mobileTopbarActive = signal(false);
  showMenu = signal(false);

  constructor() {
    if (typeof window !== 'undefined') {
      window.addEventListener('click', () => {
        this.showMenu.set(false);
      });
    }
  }

  toggleTopbar(): void {
    this.mobileTopbarActive.update((value) => !value);
  }

  toggleMenu(): void {
    this.showMenu.update((v) => !v);
  }

  handleMenuClick(action: string): void {
    this.showMenu.set(false);
    if (action === 'Profile') {
      void this.router.navigate(['/perfil']);
      return;
    }

    if (action === 'SignOut') {
      this.authService.logout();
      void this.router.navigate(['/login']);
    }
  }
}
